var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo, useState } from 'react';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { NotificationState, NotificationType } from 'root/src/shared/notification/notification.types';
// Notifications are not time-critical, so a gentle poll keeps the unread badge fresh
// without hammering the API (the pending-reviews badge polls faster because it gates work).
const POLL_INTERVAL_MS = 60 * 1000;
// The list endpoint has no server-side pagination, so cap the read history shown client-side.
const READ_HISTORY_LIMIT = 20;
// `created` is an ISO 8601 string (server getTimestamp), so a lexicographic compare is chronological.
const byCreatedDesc = (a, b) => (a.created < b.created ? 1 : -1);
export const useNotifications = ({ enabled, open }) => {
    const utils = trpc.useUtils();
    const unreadQuery = trpc.notification.list.useQuery({ state: NotificationState.Unread, type: NotificationType.Popup }, { enabled, refetchInterval: POLL_INTERVAL_MS });
    const readQuery = trpc.notification.list.useQuery({ state: NotificationState.Read, type: NotificationType.Popup }, { enabled: enabled && open });
    const readMutation = trpc.notification.read.useMutation();
    const [markingAll, setMarkingAll] = useState(false);
    const unread = useMemo(() => { var _a; return ((_a = unreadQuery.data) !== null && _a !== void 0 ? _a : []).slice().sort(byCreatedDesc); }, [unreadQuery.data]);
    // Unread first, then the most recent read items as history. Dedupe by id: a notification
    // keeps its id across the read/unread transition, so during the brief refetch window after
    // marking read it can momentarily appear in both lists (which would clash React keys).
    const items = useMemo(() => {
        var _a;
        const unreadIds = new Set(unread.map(item => item.id));
        const read = ((_a = readQuery.data) !== null && _a !== void 0 ? _a : [])
            .filter(item => !unreadIds.has(item.id))
            .sort(byCreatedDesc)
            .slice(0, READ_HISTORY_LIMIT);
        return [...unread, ...read];
    }, [unread, readQuery.data]);
    const invalidate = useCallback(() => utils.notification.list.invalidate(), [utils]);
    const markRead = useCallback((notificationId) => __awaiter(void 0, void 0, void 0, function* () {
        // The server throws if a notification is already read; swallow it and let the
        // refetch reconcile, so a stale unread badge never blocks the UI.
        try {
            yield readMutation.mutateAsync({ notificationId });
        }
        catch (_a) {
            // no-op: invalidate below brings the cache back in sync
        }
        yield invalidate();
    }), [readMutation, invalidate]);
    const markAllRead = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!unread.length)
            return;
        setMarkingAll(true);
        try {
            yield Promise.all(unread.map(item => readMutation.mutateAsync({ notificationId: item.id }).catch(() => undefined)));
            yield invalidate();
        }
        finally {
            setMarkingAll(false);
        }
    }), [unread, readMutation, invalidate]);
    return {
        items,
        unreadCount: unread.length,
        isLoading: unreadQuery.isLoading || (open && readQuery.isLoading && !readQuery.data),
        isError: unreadQuery.isError,
        markRead,
        markAllRead,
        markingAll,
    };
};
